import { ChevronDownIcon, UserRoundIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { SignOutMenuItem } from '../../features/auth/sign-out';
import { cn } from '../../shared/lib/cn';
import { Avatar, AvatarFallback } from '../../shared/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../shared/ui/dropdown-menu';

/** The signed-in person's menu at the right of the top bar: who they are, Account, Sign out. */
export function AccountMenu({
  userEmail,
  userName,
}: {
  userEmail: string;
  /** From the profile; the email stands in until it loads, or when there is none. */
  userName?: { firstName: string; lastName: string };
}) {
  const { t } = useTranslation('shell');
  const fullName = userName ? `${userName.firstName} ${userName.lastName}` : null;
  const initials = userName
    ? `${userName.firstName.charAt(0)}${userName.lastName.charAt(0)}`.toUpperCase()
    : userEmail.trim().charAt(0).toUpperCase() || '?';
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label={t('accountMenu')}
        className="flex shrink-0 cursor-pointer items-center gap-2 rounded-full p-1 text-left text-sm font-bold text-heading transition-colors duration-150 ease-standard hover:bg-muted data-[state=open]:bg-muted sm:pr-3"
      >
        <Avatar size="sm">
          <AvatarFallback>{initials}</AvatarFallback>
        </Avatar>
        {/* below `sm` the top bar has no room for the name: the avatar alone, same accessible name */}
        <span className="hidden max-w-48 truncate sm:inline">{fullName ?? userEmail}</span>
        <ChevronDownIcon aria-hidden="true" className="hidden size-4 shrink-0 sm:block" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel className="grid gap-0.5">
          {fullName ? <span className="truncate">{fullName}</span> : null}
          <span className={cn('truncate', fullName ? 'font-normal text-muted-foreground' : '')}>
            {userEmail}
          </span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/account" className="text-popover-foreground no-underline">
            <UserRoundIcon aria-hidden="true" />
            {t('account')}
          </Link>
        </DropdownMenuItem>
        <SignOutMenuItem />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
